import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "../lib/api";
import { LOGO_URL } from "../lib/permissions";
import { PageHeader } from "../components/ResourcePage";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Printer, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { toast } from "sonner";

function printReport(title, bodyHTML, range) {
  const period = range?.from || range?.to ? `${range.from || "start"} → ${range.to || "today"}` : "All time";
  const html = `<!doctype html><html><head><title>Modav Farms — ${title}</title>
  <style>body{font-family:Arial,sans-serif;color:#1A1D1A;max-width:780px;margin:24px auto;padding:0 20px}
  .head{display:flex;align-items:center;gap:14px;border-bottom:3px solid #2C5530;padding-bottom:14px;margin-bottom:8px}
  img{width:64px;height:64px;border-radius:50%}
  h1{font-size:22px;margin:0}.muted{color:#5C665E;font-size:12px;margin:2px 0}
  h2{font-size:16px;margin:22px 0 8px;color:#2C5530}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th,td{padding:7px 6px;border-bottom:1px solid #eee;text-align:left}
  td.r,th.r{text-align:right}
  .tot{font-weight:bold;border-top:2px solid #2C5530}
  .big{display:flex;justify-content:space-between;font-size:18px;font-weight:bold;margin-top:10px;padding:10px;background:#EFF3EE;border-radius:8px}
  .foot{text-align:center;color:#5C665E;font-size:11px;margin-top:28px;border-top:1px solid #eee;padding-top:10px}</style></head>
  <body><div class="head"><img src="${LOGO_URL}"/><div><h1>Modav Farms</h1>
  <p class="muted">${title} · ${period}</p><p class="muted">Generated ${new Date().toLocaleString()}</p></div></div>
  ${bodyHTML}
  <p class="foot">Modav Farms · Ilese, Ogun State · +234 7061797432 — Fresh. Ethical. Sustainable.</p>
  <script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open("", "_blank", "width=820,height=900");
  if (w) { w.document.write(html); w.document.close(); }
}

const rows = (arr, label) => arr.map((x) => `<tr><td>${x[label]}</td><td class="r">${fmtMoney(x.amount ?? x.value)}</td></tr>`).join("");

export function Reports() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ date_from: "", date_to: "" });

  const load = async () => {
    try {
      const params = {};
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const { data } = await api.get("/reports", { params });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 text-center text-muted-foreground">Loading reports…</div>;
  const { pnl, cash_flow, valuations, balance_sheet: bs, vendor_spend, payroll_cost, customer_revenue } = data;

  const printPnl = () => printReport("Profit & Loss Statement", `
    <h2>Income</h2><table>${rows(pnl.income, "category")}<tr class="tot"><td>Total Income</td><td class="r">${fmtMoney(pnl.total_income)}</td></tr></table>
    <h2>Expenses</h2><table>${rows(pnl.expense, "category")}<tr class="tot"><td>Total Expenses</td><td class="r">${fmtMoney(pnl.total_expense)}</td></tr></table>
    <div class="big"><span>Net Profit</span><span>${fmtMoney(pnl.net_profit)}</span></div>`, data.range);

  const printCash = () => printReport("Cash Flow Statement",
    `<table><tr><th>Month</th><th class="r">Inflow</th><th class="r">Outflow</th><th class="r">Net</th></tr>
    ${cash_flow.map((c) => `<tr><td>${c.month}</td><td class="r">${fmtMoney(c.inflow)}</td><td class="r">${fmtMoney(c.outflow)}</td><td class="r">${fmtMoney(c.net)}</td></tr>`).join("")}</table>`, data.range);

  const printBalance = () => printReport("Balance Sheet", `
    <h2>Assets</h2><table>
      <tr><td>Cash & net position</td><td class="r">${fmtMoney(bs.cash)}</td></tr>
      <tr><td>Inventory</td><td class="r">${fmtMoney(bs.inventory)}</td></tr>
      <tr><td>Livestock</td><td class="r">${fmtMoney(bs.livestock)}</td></tr>
      <tr><td>Accounts receivable</td><td class="r">${fmtMoney(bs.accounts_receivable)}</td></tr>
      <tr class="tot"><td>Total Assets</td><td class="r">${fmtMoney(bs.total_assets)}</td></tr></table>
    <h2>Liabilities</h2><table>
      <tr><td>Accounts payable (unpaid bills)</td><td class="r">${fmtMoney(bs.accounts_payable)}</td></tr>
      <tr class="tot"><td>Total Liabilities</td><td class="r">${fmtMoney(bs.total_liabilities)}</td></tr></table>
    <div class="big"><span>Owner's Equity</span><span>${fmtMoney(bs.equity)}</span></div>`, data.range);

  const printVal = () => printReport("Inventory & Livestock Valuation", `
    <h2>Inventory Valuation</h2><table><tr><th>Item</th><th class="r">Qty</th><th class="r">Unit cost</th><th class="r">Value</th></tr>
      ${valuations.inventory.map((i) => `<tr><td>${i.name}</td><td class="r">${i.quantity} ${i.unit}</td><td class="r">${fmtMoney(i.unit_cost)}</td><td class="r">${fmtMoney(i.value)}</td></tr>`).join("")}
      <tr class="tot"><td colspan="3">Total Inventory Value</td><td class="r">${fmtMoney(valuations.inventory_total)}</td></tr></table>
    <h2>Livestock Valuation</h2><table><tr><th>Tag</th><th>Species</th><th class="r">Head</th><th class="r">Value/head</th><th class="r">Value</th></tr>
      ${valuations.livestock.map((l) => `<tr><td>${l.tag}</td><td>${l.species}</td><td class="r">${l.count}</td><td class="r">${fmtMoney(l.value_per_head)}</td><td class="r">${fmtMoney(l.value)}</td></tr>`).join("")}
      <tr class="tot"><td colspan="4">Total Livestock Value</td><td class="r">${fmtMoney(valuations.livestock_total)}</td></tr></table>`, data.range);

  const PrintBtn = ({ onClick }) => (
    <Button variant="outline" onClick={onClick} data-testid="report-print"><Printer className="mr-1.5 h-4 w-4" />Download PDF</Button>
  );

  const printVendor = () => printReport("Vendor Spend Report", `
    <table><tr><th>Vendor</th><th>Category</th><th class="r">Bills/Txns</th><th class="r">Amount</th></tr>
      ${vendor_spend.items.map((v) => `<tr><td>${v.vendor}</td><td>${v.category || "—"}</td><td class="r">${v.count}</td><td class="r">${fmtMoney(v.amount)}</td></tr>`).join("")}
      <tr class="tot"><td colspan="3">Total Spend</td><td class="r">${fmtMoney(vendor_spend.total)}</td></tr></table>`, data.range);

  const printPayroll = () => printReport("Payroll Cost Report", `
    <table><tr><th>Employee</th><th>Employee ID</th><th class="r">Runs</th><th class="r">Gross</th><th class="r">Deductions</th><th class="r">Net</th></tr>
      ${payroll_cost.items.map((p) => `<tr><td>${p.employee}</td><td>${p.employee_id || "—"}</td><td class="r">${p.runs}</td><td class="r">${fmtMoney(p.gross)}</td><td class="r">${fmtMoney(p.deductions)}</td><td class="r">${fmtMoney(p.net)}</td></tr>`).join("")}
      <tr class="tot"><td colspan="3">Totals</td><td class="r">${fmtMoney(payroll_cost.gross_total)}</td><td class="r"></td><td class="r">${fmtMoney(payroll_cost.net_total)}</td></tr></table>`, data.range);

  const printCustomers = () => printReport("Customer Revenue Report", `
    <table><tr><th>Customer</th><th class="r">Transactions</th><th class="r">Revenue</th></tr>
      ${customer_revenue.items.map((c) => `<tr><td>${c.customer}</td><td class="r">${c.count}</td><td class="r">${fmtMoney(c.amount)}</td></tr>`).join("")}
      <tr class="tot"><td colspan="2">Total Revenue</td><td class="r">${fmtMoney(customer_revenue.total)}</td></tr></table>`, data.range);

  return (
    <div className="fade-up">
      <PageHeader title="Reports" subtitle="Financial statements and valuations — exportable to PDF." />

      <Card className="mb-6 flex flex-wrap items-end gap-4 p-4">
        <div><Label className="text-xs">From</Label><Input type="date" className="mt-1.5 w-44" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} data-testid="report-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" className="mt-1.5 w-44" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} data-testid="report-to" /></div>
        <Button onClick={load} data-testid="report-apply">Apply</Button>
        {(range.date_from || range.date_to) && <Button variant="ghost" onClick={() => { setRange({ date_from: "", date_to: "" }); setTimeout(load, 0); }}>Clear</Button>}
      </Card>

      <Tabs defaultValue="pnl">
        <TabsList className="mb-5 flex-wrap">
          <TabsTrigger value="pnl" data-testid="tab-pnl">Profit & Loss</TabsTrigger>
          <TabsTrigger value="cash" data-testid="tab-cash">Cash Flow</TabsTrigger>
          <TabsTrigger value="balance" data-testid="tab-balance">Balance Sheet</TabsTrigger>
          <TabsTrigger value="valuation" data-testid="tab-valuation">Valuations</TabsTrigger>
          <TabsTrigger value="vendor" data-testid="tab-vendor">Vendor Spend</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll Cost</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
        </TabsList>

        <TabsContent value="pnl">
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <Card className="p-5"><div className="flex items-center gap-2 text-emerald-700"><TrendingUp className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Income</span></div><p className="num mt-2 font-display text-2xl font-extrabold">{fmtMoney(pnl.total_income)}</p></Card>
            <Card className="p-5"><div className="flex items-center gap-2 text-red-600"><TrendingDown className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Expenses</span></div><p className="num mt-2 font-display text-2xl font-extrabold">{fmtMoney(pnl.total_expense)}</p></Card>
            <Card className="p-5"><div className="flex items-center gap-2 text-sky-700"><Wallet className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">Net Profit</span></div><p className="num mt-2 font-display text-2xl font-extrabold">{fmtMoney(pnl.net_profit)}</p></Card>
          </div>
          <div className="mb-4 flex justify-end"><PrintBtn onClick={printPnl} /></div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-6"><h3 className="mb-3 font-display font-bold">Income</h3><PnlTable rows={pnl.income} total={pnl.total_income} totalLabel="Total Income" /></Card>
            <Card className="p-6"><h3 className="mb-3 font-display font-bold">Expenses</h3><PnlTable rows={pnl.expense} total={pnl.total_expense} totalLabel="Total Expenses" /></Card>
          </div>
        </TabsContent>

        <TabsContent value="cash">
          <div className="mb-4 flex justify-end"><PrintBtn onClick={printCash} /></div>
          <Card className="p-6">
            <h3 className="mb-4 font-display font-bold">Cash Flow — last 6 months</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={cash_flow}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={11} tickFormatter={(m) => m.slice(5)} />
                <YAxis tickLine={false} axisLine={false} fontSize={11} tickFormatter={(v) => `₦${v / 1000}k`} />
                <Tooltip formatter={(v) => fmtMoney(v)} />
                <Legend iconType="circle" />
                <Bar dataKey="inflow" name="Inflow" fill="#2C5530" radius={[4, 4, 0, 0]} />
                <Bar dataKey="outflow" name="Outflow" fill="#C25934" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <Table className="mt-4">
              <TableHeader><TableRow><TableHead>Month</TableHead><TableHead className="text-right">Inflow</TableHead><TableHead className="text-right">Outflow</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
              <TableBody>{cash_flow.map((c) => (
                <TableRow key={c.month}><TableCell>{c.month}</TableCell><TableCell className="num text-right">{fmtMoney(c.inflow)}</TableCell><TableCell className="num text-right">{fmtMoney(c.outflow)}</TableCell><TableCell className={`num text-right font-semibold ${c.net < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtMoney(c.net)}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="balance">
          <div className="mb-4 flex justify-end"><PrintBtn onClick={printBalance} /></div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-6">
              <h3 className="mb-3 font-display font-bold">Assets</h3>
              <BsRow label="Cash & net position" value={bs.cash} />
              <BsRow label="Inventory" value={bs.inventory} />
              <BsRow label="Livestock" value={bs.livestock} />
              <BsRow label="Accounts receivable" value={bs.accounts_receivable} />
              <BsRow label="Total Assets" value={bs.total_assets} bold />
            </Card>
            <Card className="p-6">
              <h3 className="mb-3 font-display font-bold">Liabilities & Equity</h3>
              <BsRow label="Accounts payable" value={bs.accounts_payable} />
              <BsRow label="Total Liabilities" value={bs.total_liabilities} bold />
              <div className="mt-4 flex items-center justify-between rounded-md bg-secondary/50 px-4 py-3">
                <span className="font-semibold">Owner's Equity</span>
                <span className="num font-display text-xl font-extrabold text-primary">{fmtMoney(bs.equity)}</span>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="valuation">
          <div className="mb-4 flex justify-end"><PrintBtn onClick={printVal} /></div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-6">
              <h3 className="mb-3 font-display font-bold">Inventory Valuation</h3>
              {valuations.inventory.length === 0 ? <p className="text-sm text-muted-foreground">No inventory items.</p> : (
                <Table><TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Value</TableHead></TableRow></TableHeader>
                  <TableBody>{valuations.inventory.map((i, x) => <TableRow key={x}><TableCell>{i.name}</TableCell><TableCell className="num text-right">{i.quantity} {i.unit}</TableCell><TableCell className="num text-right">{fmtMoney(i.value)}</TableCell></TableRow>)}</TableBody>
                </Table>
              )}
              <div className="mt-3 flex justify-between border-t-2 border-primary pt-2 font-semibold"><span>Total</span><span className="num">{fmtMoney(valuations.inventory_total)}</span></div>
            </Card>
            <Card className="p-6">
              <h3 className="mb-3 font-display font-bold">Livestock Valuation</h3>
              {valuations.livestock.length === 0 ? <p className="text-sm text-muted-foreground">No livestock recorded.</p> : (
                <Table><TableHeader><TableRow><TableHead>Tag</TableHead><TableHead>Species</TableHead><TableHead className="text-right">Value</TableHead></TableRow></TableHeader>
                  <TableBody>{valuations.livestock.map((l, x) => <TableRow key={x}><TableCell>{l.tag}</TableCell><TableCell>{l.species}</TableCell><TableCell className="num text-right">{fmtMoney(l.value)}</TableCell></TableRow>)}</TableBody>
                </Table>
              )}
              <div className="mt-3 flex justify-between border-t-2 border-primary pt-2 font-semibold"><span>Total</span><span className="num">{fmtMoney(valuations.livestock_total)}</span></div>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="vendor">
          <div className="mb-4 flex items-center justify-between">
            <Card className="p-4"><span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total vendor spend</span><p className="num mt-1 font-display text-2xl font-extrabold">{fmtMoney(vendor_spend.total)}</p></Card>
            <PrintBtn onClick={printVendor} />
          </div>
          <Card className="p-6">
            <h3 className="mb-3 font-display font-bold">Spend by vendor</h3>
            {vendor_spend.items.length === 0 ? <p className="text-sm text-muted-foreground">No vendor spend in this period.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Vendor</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Bills/Txns</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>{vendor_spend.items.map((v, i) => (
                  <TableRow key={i} data-testid="vendor-spend-row"><TableCell className="font-medium">{v.vendor}</TableCell><TableCell className="text-sm">{v.category || "—"}</TableCell><TableCell className="num text-right">{v.count}</TableCell><TableCell className="num text-right font-semibold">{fmtMoney(v.amount)}</TableCell></TableRow>
                ))}
                <TableRow className="border-t-2 border-primary"><TableCell colSpan={3} className="font-semibold">Total</TableCell><TableCell className="num text-right font-bold">{fmtMoney(vendor_spend.total)}</TableCell></TableRow>
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="payroll">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex gap-3">
              <Card className="p-4"><span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Gross payroll</span><p className="num mt-1 font-display text-2xl font-extrabold">{fmtMoney(payroll_cost.gross_total)}</p></Card>
              <Card className="p-4"><span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Net paid</span><p className="num mt-1 font-display text-2xl font-extrabold">{fmtMoney(payroll_cost.net_total)}</p></Card>
            </div>
            <PrintBtn onClick={printPayroll} />
          </div>
          <Card className="p-6">
            <h3 className="mb-3 font-display font-bold">Payroll cost by employee</h3>
            {payroll_cost.items.length === 0 ? <p className="text-sm text-muted-foreground">No payroll runs in this period.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Employee ID</TableHead><TableHead className="text-right">Runs</TableHead><TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Deductions</TableHead><TableHead className="text-right">Net</TableHead></TableRow></TableHeader>
                <TableBody>{payroll_cost.items.map((p, i) => (
                  <TableRow key={i} data-testid="payroll-cost-row"><TableCell className="font-medium">{p.employee}</TableCell><TableCell className="text-sm font-mono">{p.employee_id || "—"}</TableCell><TableCell className="num text-right">{p.runs}</TableCell><TableCell className="num text-right">{fmtMoney(p.gross)}</TableCell><TableCell className="num text-right">{fmtMoney(p.deductions)}</TableCell><TableCell className="num text-right font-semibold">{fmtMoney(p.net)}</TableCell></TableRow>
                ))}
                <TableRow className="border-t-2 border-primary"><TableCell colSpan={3} className="font-semibold">Totals</TableCell><TableCell className="num text-right font-bold">{fmtMoney(payroll_cost.gross_total)}</TableCell><TableCell /><TableCell className="num text-right font-bold">{fmtMoney(payroll_cost.net_total)}</TableCell></TableRow>
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="customers">
          <div className="mb-4 flex items-center justify-between">
            <Card className="p-4"><span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total customer revenue</span><p className="num mt-1 font-display text-2xl font-extrabold">{fmtMoney(customer_revenue.total)}</p></Card>
            <PrintBtn onClick={printCustomers} />
          </div>
          <Card className="p-6">
            <h3 className="mb-3 font-display font-bold">Revenue by customer</h3>
            {customer_revenue.items.length === 0 ? <p className="text-sm text-muted-foreground">No customer revenue in this period.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead className="text-right">Transactions</TableHead><TableHead className="text-right">Revenue</TableHead></TableRow></TableHeader>
                <TableBody>{customer_revenue.items.map((c, i) => (
                  <TableRow key={i} data-testid="customer-revenue-row"><TableCell className="font-medium">{c.customer}</TableCell><TableCell className="num text-right">{c.count}</TableCell><TableCell className="num text-right font-semibold">{fmtMoney(c.amount)}</TableCell></TableRow>
                ))}
                <TableRow className="border-t-2 border-primary"><TableCell colSpan={2} className="font-semibold">Total</TableCell><TableCell className="num text-right font-bold">{fmtMoney(customer_revenue.total)}</TableCell></TableRow>
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PnlTable({ rows, total, totalLabel }) {
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">No records in this period.</p>;
  return (
    <Table>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}><TableCell>{r.category}</TableCell><TableCell className="num text-right">{fmtMoney(r.amount)}</TableCell></TableRow>
        ))}
        <TableRow className="border-t-2 border-primary"><TableCell className="font-semibold">{totalLabel}</TableCell><TableCell className="num text-right font-bold">{fmtMoney(total)}</TableCell></TableRow>
      </TableBody>
    </Table>
  );
}

function BsRow({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between py-2 ${bold ? "border-t-2 border-primary font-semibold" : "border-b border-border"}`}>
      <span className="text-sm">{label}</span>
      <span className="num text-sm">{fmtMoney(value)}</span>
    </div>
  );
}
